<template>
    <div class="row">
        <div class="col-12 col-md-12 note-head" :class="isInlineHistory && 'history-head'">
            <div class="note-title">
                <h4>{{ note.name }}</h4>
                <div class="history-search-wrapper" v-if="isInlineHistory" ref="historyDropdownRef">
                    <div class="input-group input-group-merge">
                        <span class="input-group-text">
                            <img src="@/assets/search-icon.svg" alt="Search" width="14" height="14" />
                        </span>
                        <input
                            v-model="searchTxt"
                            @input="onSearchNote"
                            @focus="onFocusSearch"
                            @keyup.enter="onEnterSearch"
                            type="text"
                            class="form-control"
                            placeholder="Search or add new"
                        />
                    </div>
                    <ul v-if="isNoteListOpen" class="inline-note-list deep-shadow">
                        <li v-if="noteLoading && !noteResults.length" class="empty-text">Loading...</li>
                        <li
                            v-for="item in noteResults" :key="item.id"
                            @click="onSelectNoteItem(item)"
                            class="note-item"
                        >
                            <div class="form-check">
                                <input :checked="item.is_selected" class="form-check-input" type="checkbox">
                                <label :class="item.is_selected && 'text-primary text-bold'" class="form-check-label">
                                    {{ item.name }}
                                </label>
                            </div>
                        </li>
                        <li v-if="canAddNewNote" @click="onAddNewNote" class="note-item add-new-item">
                            <i class="fas fa-plus"></i>
                            Add "{{ searchTxt }}"
                        </li>
                        <li v-if="!noteLoading && !noteResults.length && !canAddNewNote" class="empty-text">No note found</li>
                    </ul>
                </div>
                <AsyncSelect
                    v-if="isInlineHistory"
                    class="select-template"
                    placeholder="Select Template Name"
                    v-model="groupTemplate"
                    :additional-query="{ template_type: note.value }"
                    :api-service="fetchTemplateList"
                    @option:selected="onSelectTemplate"
                />
            </div>
            <div class="note-btn d-flex align-items-center" v-if="!note.isHideAddBtn">
                <button v-if="canUploadAttachment" @click="onOpenAttachmentModal" type="button" class="btn btn-outline-secondary btn-sm btn-block me-1">
                    <i class="fas fa-paperclip"></i>
                </button>
                <button v-if="!isInlineHistory"  @click="onOpenModal(note, index)" type="button" class="btn btn-outline-primary btn-sm btn-block">
                    <i class="fas fa-plus"></i>
                </button>
            </div>
        </div>
        <div class="col-21">
            <hr class="mt-0">
        </div>
        <div class="col-12 col-md-12 note-body">
            <div class="card">
                <div class="card-body">
                    <div class="medicine" v-for="(item, noteIndex) in note.selectedNotes" :key="noteIndex">
                        <div class="medicine-name" v-if="isSpecialNote">
                            <span class="sr-no">{{ noteIndex + 1 }}. </span>
                            <span class="name">{{ item.name }}</span>
                            <span v-if="isSpecialNote">
                                <input
                                    v-model.trim="item.result" type="text"
                                    class="note-input-investigation-history form-control"
                                    ref="input"
                                >
                            </span>
                        </div>
                        <div class="medicine-name" v-else>
                            <span class="sr-no">{{ noteIndex + 1 }}. </span>
                            <span v-if="currentIndex !== noteIndex" @click="setEdit(item, noteIndex)" class="name">{{ item }}</span>
                            <span v-show="currentIndex === noteIndex">
                                <input 
                                    v-model.trim="editValue" type="text" 
                                    @blur="onUpdate(item, noteIndex)"
                                    @keyup.enter="onUpdate(item, noteIndex)"
                                    class="note-input form-control"
                                    ref="input"
                                >
                            </span>
                        </div>
                        <div @click="onRemoveNote(item, noteIndex)" class="action" v-if="!note.isHideAddBtn">
                            <i class="far fa-times-circle close"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="col-12 note-footer pb-2" v-if="from === 'group-template'">
            <SubmitCancelBtn
                class="text-right"
                @onSubmit="onSaveTemplate"
                :loading="loading"
                :isHideCancelBtn="true"
                :disabled="note.selectedNotes.length === 0"
            />
        </div>
        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, watch } from "vue";
import { ref } from "vue"
import { debounce } from 'lodash';
import useDetectOutsideClick from '@/services/useDetectOutsideClick';
import SubmitCancelBtn from '@/components/atom/SubmitCancelBtn.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useRoute } from "vue-router";
import { convertToTitleCase } from '@/services/utils/global';
import Loader from '@/components/atom/LoaderComponent'
import handleHospital from '@/services/modules/hospital'
import handlePurchase from '@/services/modules/purchase'

const props = defineProps({
    note: Object,
    index: {
        type: Number,
        default: -1,
    },
    from: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['openModal', 'openAttachmentModal'])
const currentIndex = ref(-1);
const editValue = ref(null);
const input = ref(null)
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);

const { fetchTemplateList, fetchSingleTemplate, notesByType } = handleHospital();
const { fetchProductBySlug, fetchProducts } = handlePurchase();
const groupTemplate = ref(null);
const searchTxt = ref('');
const noteResults = ref([]);
const noteLoading = ref(false);
const isNoteListOpen = ref(false);
const historyDropdownRef = ref(null);

const isSpecialNote = computed(() => {
    return ['investigation_history', 'drug_history'].includes(props.note.value);
})

const inlineNoteTypes = ['investigation_history', 'advice', 'medical_history', 'drug_history'];

const isInlineHistory = computed(() => {
    if (props.from === 'group-template') return false;
    if (props.note.isHideAddBtn) return false;
    return inlineNoteTypes.includes(props.note.value);
})

const isObjectNote = computed(() => {
    return ['investigation_history', 'drug_history'].includes(props.note.value);
})

const getSelectedName = (note) => {
    if (note && note.name) return note.name;
    return note;
}

const canAddNewNote = computed(() => {
    if (!searchTxt.value) return false;
    const typed = searchTxt.value.toLowerCase();
    const inResults = noteResults.value.some(function (item) {
        return item.name.toLowerCase() === typed;
    });
    if (inResults) return false;
    return !props.note.selectedNotes.some(function (item) {
        const name = getSelectedName(item);
        return name && name.toLowerCase() === typed;
    });
})

const isTemplateItemSelected = (item) => {
    return props.note.selectedNotes.some(function (note) {
        if (isObjectNote.value && note.id && item.id) return note.id === item.id;
        return getSelectedName(note) === item.name;
    });
}

const markSelectedNotes = (items) => {
    return items.map(function (item) {
        return {
            ...item,
            is_selected: isTemplateItemSelected(item)
        };
    });
}

const getNoteRows = (data) => {
    if (data.data) return data.data;
    return data;
}

const fetchInvestigationNoteList = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&product_type=services&slug=investigation`;
    query += `&service_type=${props.note.value}`;
    query += `&q=${encodeURIComponent(searchTxt.value)}`;
    return fetchProductBySlug(query);
}

const fetchMasterNoteList = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&limit=100`;
    query += `&searchTxt=${encodeURIComponent(searchTxt.value)}`;
    return notesByType(query, props.note.value);
}

const fetchDrugNoteList = () => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&per_page=100`;
    query += `&product_type=finished_goods&service_type=rx`;
    query += `&q=${encodeURIComponent(searchTxt.value)}`;
    return fetchProducts(query);
}

const getNoteListRequest = () => {
    if (props.note.value === 'drug_history') return fetchDrugNoteList();
    if (isObjectNote.value) return fetchInvestigationNoteList();
    return fetchMasterNoteList();
}

const fetchNoteList = async () => {
    noteLoading.value = true;
    await getNoteListRequest().then(res => {
        if (!res.status || !res.data) {
            noteResults.value = [];
            return;
        }
        noteResults.value = markSelectedNotes(getNoteRows(res.data));
    }).catch(() => {
        noteResults.value = [];
    }).finally(() => {
        noteLoading.value = false;
    })
}

const onSearchNote = debounce(() => {
    isNoteListOpen.value = true;
    fetchNoteList();
}, 300)

const onFocusSearch = () => {
    isNoteListOpen.value = true;
}

const closeNoteList = () => {
    isNoteListOpen.value = false;
}

useDetectOutsideClick(historyDropdownRef, () => {
    closeNoteList();
})

const getDrugPrice = (item) => {
    if (!item.price) return 0;
    return item.price;
}

const makeDrugNote = (item) => {
    return {
        id: item.id,
        name: item.name,
        price: getDrugPrice(item),
        is_selected: true,
        product_type: item.product_type,
        stock_in_hand: item.stock_in_hand,
        product_type_title: item.product_type_title,
        result: ''
    };
}

const makeSelectedNote = (item) => {
    if (props.note.value === 'drug_history') return makeDrugNote(item);
    if (isObjectNote.value) return { id: item.id, name: item.name, type: props.note.value, result: '' };
    return item.name;
}

const onSelectNoteItem = (item) => {
    const existingIndex = props.note.selectedNotes.findIndex(function (note) {
        if (isObjectNote.value && note.id && item.id) return note.id === item.id;
        return getSelectedName(note) === item.name;
    });
    if (existingIndex !== -1) {
        props.note.selectedNotes.splice(existingIndex, 1);
        item.is_selected = false;
        closeNoteList();
        return;
    }
    props.note.selectedNotes.push(makeSelectedNote(item));
    item.is_selected = true;
    closeNoteList();
}

const onEnterSearch = async () => {
    if (!searchTxt.value) return;
    onSearchNote.flush();
    await fetchNoteList();
    const typed = searchTxt.value.toLowerCase();
    const matched = noteResults.value.find(function (item) {
        return item.name.toLowerCase() === typed;
    });
    if (matched) {
        onSelectNoteItem(matched);
        searchTxt.value = '';
        return;
    }
    if (!canAddNewNote.value) return;
    onAddNewNote();
}

const onAddNewNote = () => {
    const newName = searchTxt.value;
    props.note.selectedNotes.push(makeSelectedNote({ id: null, name: newName }));
    props.note.newNotesByDoctor.push(newName);
    searchTxt.value = '';
    closeNoteList();
}

const onSelectTemplate = async (template) => {
    let query = `?company_id=${route.params.companyId}`;
    query += `&template_type=${props.note.value}`;
    loading.value = true;
    await fetchSingleTemplate(query, template.id).then(res => {
        if (!res.status) return showError(res.message);
        setTemplateItems(res.data);
    }).catch(err => {
        showError('Failed to load template');
    }).finally(() => {
        loading.value = false;
        groupTemplate.value = null;
    })
}

const setTemplateItems = (items) => {
    for (const item of items) {
        if (isTemplateItemSelected(item)) continue;
        props.note.selectedNotes.push(makeSelectedNote(item));
    }
    syncSelectedNotes();
}

const syncSelectedNotes = () => {
    noteResults.value.forEach(function (item) {
        item.is_selected = isTemplateItemSelected(item);
    });
}

const canUploadAttachment = computed(() => {
    if (props.note.value !== 'investigation_history') return false;
    return Boolean(props.note.id);
})

const onOpenModal = (note, index = -1) => {
    if(note.value){
        note.name = convertToTitleCase(note.value);
    }
    emit('openModal', {note, index})
}

const onOpenAttachmentModal = () => {
    emit('openAttachmentModal', {note: props.note, index: props.index})
}

const syncRemovedNote = (removedItem) => {
    if (!isInlineHistory.value) return;
    const listedNote = noteResults.value.find(function (item) {
        if (isObjectNote.value && removedItem.id && item.id) return removedItem.id === item.id;
        return getSelectedName(removedItem) === item.name;
    });
    if (listedNote) {
        listedNote.is_selected = false;
    }
}

const getNoteLookupName = (noteName) => {
    if (noteName && noteName.name) return noteName.name;
    return noteName;
}

const onRemoveNote = (noteName, selectedIndex) => {
    const removedItem = props.note.selectedNotes[selectedIndex];
    props.note.selectedNotes.splice(selectedIndex, 1)
    syncRemovedNote(removedItem);

    const index = props.note.newNotesByDoctor.indexOf(getNoteLookupName(noteName));
    if(index < 0) return;

    props.note.newNotesByDoctor.splice(index, 1);
}

const setEdit = (note, index) => {
    if(props.note.isHideAddBtn || isSpecialNote.value) return;

    currentIndex.value = index;
    editValue.value = note;

    if (input.value && input.value[index]) {
        input.value[index].focus();
    }
}

const onUpdate = (item, index) => {
    currentIndex.value = -1;
    const previousName = getSelectedName(item);
    props.note.selectedNotes.splice(index, 1, editValue.value)

    const newIndex = props.note.newNotesByDoctor.indexOf(previousName);
    if (newIndex > -1) {
        props.note.newNotesByDoctor.splice(newIndex, 1, editValue.value);
    }

    if (!isInlineHistory.value) return;
    syncSelectedNotes();
}

onMounted(() => {
    if (!isInlineHistory.value) return;
    fetchNoteList();
})

</script>

<style scoped>
.note-title {
    min-width: 200px;
}
hr.mt-0 {
    display: none;
}
.note-head.history-head {
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 14px;
    margin-top: 0;
    margin-bottom: 0;
}
.history-head .note-title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    flex: 1 1 auto;
    min-width: 0;
}
.history-head .note-title h4 {
    margin-bottom: 0;
    white-space: nowrap;
    flex: 0 0 130px;
    font-size: 14px;
    font-weight: 600;
    color: #5e5873;
}
.history-head .select-template {
    flex: 1 1 180px;
    min-width: 0;
    max-width: 260px;
}
.history-search-wrapper {
    position: relative;
    flex: 1 1 200px;
    min-width: 0;
}
.history-head .input-group {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec;
    min-height: 38px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.history-head .input-group:hover {
    border-color: #d8d6de;
}
.history-head .input-group:focus-within {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.history-head .input-group .input-group-text {
    background: transparent;
    border: none;
    padding-right: 4px;
    opacity: 0.55;
}
.history-head .input-group .form-control {
    background: transparent;
    border: none;
    padding-left: 4px;
    font-size: 13px;
    box-shadow: none;
}
.history-head .input-group .form-control::placeholder {
    color: #b9b9c3;
}
.inline-note-list {
    position: absolute;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    padding: 4px;
    margin: 6px 0 0;
    background: #ffffff;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    box-shadow: 0 5px 20px 0 rgba(34, 41, 47, 0.12);
    list-style: none;
    z-index: 99999;
}
.note-item {
    padding: 7px 8px;
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s ease;
    word-break: break-word;
}
.note-item:hover {
    background: #f8f8fa;
}
.note-item .form-check-label {
    cursor: pointer;
}
.add-new-item {
    color: #7367f0;
    font-weight: 500;
    border-top: 1px solid #f1f0f5;
    margin-top: 2px;
}
.empty-text {
    padding: 12px 8px;
    text-align: center;
    color: #b9b9c3;
    font-size: 13px;
}
.history-head .note-btn {
    flex: 0 0 auto;
}
.history-head .select-template :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    background: #f8f8fa;
    border: 1px solid #e4e2ec !important;
    min-height: 38px;
    padding: 3px 4px;
    transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.history-head .select-template :deep(.vs__dropdown-toggle:hover) {
    border-color: #d8d6de !important;
}
.history-head .select-template :deep(.vs--open .vs__dropdown-toggle) {
    background: #ffffff;
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}
.history-head .select-template :deep(.vs__selected-options) {
    padding-left: 6px;
}
.history-head .select-template :deep(.vs__actions) {
    padding-right: 8px;
}
.history-head .select-template :deep(.vs__open-indicator) {
    fill: #6e6b7b;
    transform: scale(0.9);
}
.history-head .select-template :deep(.vs__search::placeholder) {
    color: #b9b9c3;
    font-size: 13px;
}
@media (max-width: 1199px) {
    .history-head .note-title h4 {
        flex: 1 1 100%;
    }
    .history-search-wrapper,
    .history-head .select-template {
        flex: 1 1 100%;
        max-width: none;
    }
}
@media (max-width: 767px) {
    .note-head.history-head {
        padding: 10px 12px;
    }
}
.group-template {
    width: 180%;
    margin-left: -50%;
}
.no-dots {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
.note-input{
    position: absolute;
    width: 78%;
    padding: 0.271rem 1rem;
    display: inherit;
    top: -5px;
    left: 3%;
}
.note-input-investigation-history{
    position: absolute;
    width: 43%;
    padding: 0.271rem 1rem;
    display: inherit;
    top: -5px;
    left: 52%;
}
.note-body .card-body{
    padding: 4px 4px 8px;
}
.note-head {
    display: flex;
    margin-top: 0;
    justify-content: space-between;
    margin-bottom: 0;
    padding: 10px 14px;
    align-items: center;
    gap: 10px;
}
.note-head h4 {
    font-size: 15px;
    font-weight: 600;
    color: #5e5873;
    margin-bottom: 0;
}
.action .close {
    color: #ea5455;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.medicine:hover .action .close {
    opacity: 1;
}
.note-body .card {
    background: transparent;
    border: none;
    box-shadow: none;
    margin-bottom: 0;
}
.note-body .card-body:empty {
    padding: 0;
}
.medicine {
    display: flex;
    justify-content: space-between;
    margin: 0;
    padding: 6px 10px;
    border-radius: 6px;
    align-items: center;
    position: relative;
    font-size: 13px;
    color: #5e5873;
    transition: background 0.15s ease;
}
.medicine:hover {
    background: #f8f8fa;
}
.medicine .sr-no {
    color: #b9b9c3;
    font-weight: 500;
}
</style>